import { Nullable } from "@/types/util"
import parsePhoneNumberFromString, {
  CountryCode,
  isSupportedCountry,
} from "libphonenumber-js"
import { z } from "zod"

import {
  AddressCountrySchema,
  BookingFormOptions,
  EmailSchema,
} from "./form-utils"

export type { CountryCode }

/**
 * Countries whose numbers can sign in or be verified. Mirrors the backend's
 * PRELUDE_ALLOWED_COUNTRIES, which stays the rule that is enforced.
 */
export const PHONE_AUTH_COUNTRIES = [
  "US",
  "CA",
  "FR",
  "GB",
] as const satisfies readonly CountryCode[]

export const PHONE_AUTH_COUNTRIES_MESSAGE =
  "We support US, Canadian, French, and UK phone numbers."

export function isPhoneAuthCountry(country: string | undefined) {
  return PHONE_AUTH_COUNTRIES.some((supported) => supported === country)
}

export function formatPhone(value: string, defaultCountry: CountryCode = "US") {
  const phoneNumber = parsePhoneNumberFromString(value, defaultCountry)
  if (phoneNumber && phoneNumber.isValid()) {
    if (phoneNumber.country === "US") {
      return phoneNumber.formatNational()
    } else {
      return phoneNumber.formatInternational()
    }
  } else {
    return value
  }
}

export function toCountryCode(
  code: string | undefined
): CountryCode | undefined {
  return code && isSupportedCountry(code) ? code : undefined
}

export const PhoneSchema = z.object({
  phone_number: z
    .string()
    .refine((phone) => {
      let phoneNumber = parsePhoneNumberFromString(phone)
      if (phoneNumber && phoneNumber.isValid()) {
        return true
      }

      phoneNumber = parsePhoneNumberFromString(phone, "US")
      return phoneNumber ? phoneNumber.isValid() : false
    }, "Please enter a valid phone number")
    .transform((phone) => {
      let phoneNumber = parsePhoneNumberFromString(phone)

      if (!phoneNumber || !phoneNumber.isValid()) {
        phoneNumber = parsePhoneNumberFromString(phone, "US")
      }

      if (phoneNumber && phoneNumber.isValid()) {
        if (phoneNumber.country === "US") {
          return phoneNumber.nationalNumber.toString()
        } else {
          return phoneNumber.format("E.164")
        }
      }

      return phone.trim()
    }),
})

/** US national digits; a leading country code 1 is dropped from 11-digit input. */
function billingPhoneDigits(phone: string) {
  const digits = phone.replace(/\D/g, "")
  return digits.length === 11 && digits.startsWith("1")
    ? digits.slice(1)
    : digits
}

// slightly different requirements for priceline booking
export const BillingPhoneSchema = z.object({
  phone_number: z
    .string()
    .min(1)
    .refine((phone) => {
      return billingPhoneDigits(phone).length === 10
    }, "Phone number must be 10 digits") // this is required for Priceline Booking
    .refine((phone) => {
      const phoneNumber = parsePhoneNumberFromString(phone, "US")
      return phoneNumber ? phoneNumber.isValid() : false
    }, "Invalid phone number")
    .transform(billingPhoneDigits),
})

export const BillingFormSchema = AddressCountrySchema.extend(
  EmailSchema.shape
).extend(BillingPhoneSchema.shape)

export interface BillingFormValue {
  street1: string
  street2: string
  email: string
  city: string
  state: string | undefined
  zip_code: string
  phone_number: string
  country_code: string
}

export type BillingFormErrors = z.core.$ZodFormattedError<
  z.output<typeof BillingFormSchema>
>

export type BillingFormValidated = z.infer<typeof BillingFormSchema>

export function validateBillingFormFormValue(
  value: BillingFormValue,
  formOptions: BookingFormOptions,
  type: "flight" | "hotel"
): [true, BillingFormValidated, null] | [false, null, BillingFormErrors] {
  const schemaWithOptions = BillingFormSchema.superRefine((val, ctx) => {
    const countryMatch = formOptions.locations.find(
      (option) => option.country_code === val.country_code
    )
    if (!countryMatch) {
      ctx.addIssue({
        code: "custom",
        message: "Invalid country code",
        path: ["country_code"],
      })
    } else if (
      countryMatch.regions?.length &&
      !countryMatch.regions.find((region) => region.code === val.state)
    ) {
      ctx.addIssue({
        code: "custom",
        message: "Invalid state code",
        path: ["state"],
      })
    }

    if (type === "flight") {
      if (val.city && !/^[A-Za-z .',\\]+$/.test(val.city)) {
        ctx.addIssue({
          code: "custom",
          message:
            "City name can only contain letters (A-Z, a-z), periods (.), apostrophes ('), and commas (,)",
          path: ["city"],
        })
      }
    }
  })

  const res = schemaWithOptions.safeParse(value)

  if (res.success) {
    return [true, res.data, null]
  }

  return [false, null, z.formatError(res.error)]
}

export function emptyBillingFormValue(): BillingFormValue {
  return {
    street1: "",
    street2: "",
    email: "",
    city: "",
    state: undefined,
    zip_code: "",
    phone_number: "",
    country_code: "US",
  }
}

export type BillingState = [
  BillingFormValue,
  Nullable<BillingFormValidated>,
  Nullable<BillingFormErrors>,
]

export type ValidatedBillingState = [
  BillingFormValue,
  BillingFormValidated,
  null,
]
